import { useEffect, useRef, useState } from "react";
import { Camera, Sparkles, ListChecks } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Modal, inp, lbl } from "./GearBits";

/* Add many items at once: reusable templates OR AI suggestions from the trip profile. */
export default function AddItemsModal({ travelers, trips, bags, onDone, onClose }) {
  const [tab, setTab] = useState("templates");
  const [templates, setTemplates] = useState([]);
  const [tripId, setTripId] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [bagId, setBagId] = useState("");
  const [notes, setNotes] = useState("");
  const [suggestions, setSuggestions] = useState(null);
  const [picked, setPicked] = useState({});
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  useEffect(() => { api.get("/gear/templates").then((r) => setTemplates(r.data.templates)).catch(() => {}); }, []);

  const ownerName = () => (ownerId === "shared" ? "Shared" : travelers.find((t) => t.id === ownerId)?.name || null);

  const applyTemplate = async (key) => {
    setBusy(true);
    try {
      const { data } = await api.post("/gear/templates/apply", { key, trip_id: tripId || null, owner_id: ownerId || null, owner_name: ownerName(), bag_id: bagId || null });
      toast.success(`${data.created} items added to your inventory`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not apply template"); }
    finally { setBusy(false); }
  };

  const askAI = async () => {
    setBusy(true); setSuggestions(null);
    try {
      const { data } = await api.post("/gear/ai/suggest", { trip_id: tripId || null, notes });
      setSuggestions(data.suggestions);
      setPicked(Object.fromEntries(data.suggestions.map((_, i) => [i, true])));
      if (!data.ai_available) toast.error("AI is unavailable right now — try a template instead");
    } catch { toast.error("AI suggestion failed"); }
    finally { setBusy(false); }
  };

  const scanPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoPreview(URL.createObjectURL(file));
    setBusy(true); setSuggestions(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/gear/ai/photo-items", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 90000 });
      setSuggestions(data.suggestions);
      setPicked(Object.fromEntries(data.suggestions.map((_, i) => [i, true])));
      if (!data.ai_available) toast.error("AI could not analyze the photo right now");
      else if (data.suggestions.length === 0) toast.message("No items recognized — try a clearer, closer photo");
    } catch { toast.error("Photo analysis failed"); }
    finally { setBusy(false); }
  };

  const addPicked = async () => {
    const rows = (suggestions || []).filter((_, i) => picked[i])
      .map((s) => ({ name: s.name, category: s.category, quantity: s.quantity, trip_id: tripId || null, owner_id: ownerId || null, owner_name: ownerName(), bag_id: bagId || null }));
    if (!rows.length) return toast.error("Pick at least one item");
    setBusy(true);
    try {
      const { data } = await api.post("/gear/items/bulk", { items: rows });
      toast.success(`${data.created} items added`);
      onDone();
    } catch { toast.error("Could not add items"); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Add many items at once" onClose={onClose} wide>
      <div className="flex gap-2 mb-4">
        <button onClick={() => setTab("templates")} data-testid="additems-tab-templates"
          className={`flex-1 py-2 text-sm font-bold border ${tab === "templates" ? "bg-primary text-white border-primary" : "border-zinc-300"}`}>
          <ListChecks className="w-4 h-4 inline mr-1" /> Templates
        </button>
        <button onClick={() => setTab("ai")} data-testid="additems-tab-ai"
          className={`flex-1 py-2 text-sm font-bold border ${tab === "ai" ? "bg-violet-600 text-white border-violet-600" : "border-zinc-300"}`}>
          <Sparkles className="w-4 h-4 inline mr-1" /> AI suggestions
        </button>
        <button onClick={() => setTab("photo")} data-testid="additems-tab-photo"
          className={`flex-1 py-2 text-sm font-bold border ${tab === "photo" ? "bg-emerald-600 text-white border-emerald-600" : "border-zinc-300"}`}>
          <Camera className="w-4 h-4 inline mr-1" /> From photo
        </button>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <div><label className={lbl}>For trip</label>
          <select value={tripId} onChange={(e) => setTripId(e.target.value)} data-testid="additems-trip" className={inp}>
            <option value="">— No trip —</option>
            {trips.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
          </select></div>
        <div><label className={lbl}>Owner</label>
          <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)} data-testid="additems-owner" className={inp}>
            <option value="">— No owner —</option>
            <option value="shared">Shared</option>
            {travelers.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select></div>
        <div><label className={lbl}>Into bag</label>
          <select value={bagId} onChange={(e) => setBagId(e.target.value)} data-testid="additems-bag" className={inp}>
            <option value="">— No bag yet —</option>
            {bags.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select></div>
      </div>

      {tab === "templates" && (
        <div className="grid sm:grid-cols-3 gap-2" data-testid="template-grid">
          {templates.map((t) => (
            <button key={t.key} onClick={() => applyTemplate(t.key)} disabled={busy} data-testid={`template-${t.key}`}
              className="border border-zinc-300 p-3 text-left hover:border-primary hover:bg-blue-50/50 transition-colors disabled:opacity-50">
              <div className="text-sm font-bold">{t.label}</div>
              <div className="text-xs text-muted-foreground">{t.count} items</div>
            </button>
          ))}
        </div>
      )}
      {tab === "photo" && (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Photograph your open suitcase (or a pile of items) — AI lists what it sees, you pick what to add.</p>
          <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="photo-scan-btn"
            className="w-full py-2.5 text-sm font-bold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
            {busy ? "Analyzing photo…" : "Take / upload a photo"}
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" className="hidden" onChange={scanPhoto} />
          {photoPreview && <img src={photoPreview} alt="" className="max-h-40 mx-auto border border-zinc-200" />}
        </div>
      )}
      {tab !== "templates" && (
        <div className="space-y-3 mt-3">
          {tab === "ai" && (
            <>
              <div><label className={lbl}>Anything special? (optional)</label>
                <input value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="ai-notes" className={inp}
                  placeholder="Hiking, 2 kids under 5, formal dinner, cold destination…" /></div>
              <button onClick={askAI} disabled={busy} data-testid="ai-suggest-btn"
                className="w-full py-2.5 text-sm font-bold bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50">
                {busy && !suggestions ? "Thinking…" : "Suggest a packing list with AI"}
              </button>
            </>
          )}
          {suggestions && (
            <>
              <div className="max-h-64 overflow-y-auto border border-zinc-200 divide-y divide-zinc-100" data-testid="ai-suggestions">
                {suggestions.length === 0 && <p className="p-3 text-sm text-muted-foreground">
                  {tab === "photo" ? "No items recognized — try a clearer, closer photo of your items." : "No suggestions returned — try adding trip details."}</p>}
                {suggestions.map((s, i) => (
                  <label key={i} className="flex items-start gap-2 p-2.5 text-sm cursor-pointer hover:bg-zinc-50">
                    <input type="checkbox" checked={!!picked[i]} onChange={() => setPicked({ ...picked, [i]: !picked[i] })} className="w-4 h-4 mt-0.5 accent-violet-600" />
                    <span><strong>{s.name}</strong> ×{s.quantity} <span className="text-xs text-muted-foreground">({s.category})</span>
                      {s.reason && <span className="block text-xs text-muted-foreground">{s.reason}</span>}</span>
                  </label>
                ))}
              </div>
              {suggestions.length > 0 && (
                <button onClick={addPicked} disabled={busy} data-testid="ai-add-picked"
                  className="w-full py-2.5 text-sm font-bold bg-primary text-white hover:bg-blue-700 disabled:opacity-50">
                  Add {Object.values(picked).filter(Boolean).length} selected items
                </button>
              )}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
